import { tenantApi } from '@shared/api/tenantApi';
import { unwrap, unwrapList } from '@shared/api/unwrap';

/**
 * Member properties — canonical routes:
 *   - `GET  /public/properties`                 searchable estate locations (claim picker)
 *   - `GET  /public/property-relationship-types`  ownership types + required documents
 *   - `GET  /public/me/property-claims`           member's claims (enriched)
 *   - `POST /public/me/property-claims`           submit claim
 *   - `POST /public/me/property-claims/:id/cancel`
 *   - `POST /public/me/onboarding-documents`      upload supporting document
 */

export type PropertyResidencyKind = 'resident' | 'non_resident' | 'temporary_resident';

/** Approved property linked to the member (for pickers elsewhere in the app). */
export interface MemberProperty {
  property_id: string;
  unit_label: string;
  address?: string;
  type_name?: string;
  estate_name?: string;
  status?: PropertyClaim['status'] | 'active';
  region?: string;
  thumbnail_url?: string;
}

export interface EstatePropertyOption {
  location_id: string;
  name: string;
  address?: string;
  type_name?: string;
}

export interface RequiredClaimDocument {
  document_type_id: string;
  name: string;
}

export interface PropertyRelationshipTypeOption {
  relationship_type_id: string;
  code: string;
  label: string;
  required_documents: RequiredClaimDocument[];
}

export interface PropertyClaim {
  claim_id: string;
  location_id: string;
  property_id: string;
  unit_label: string;
  estate_name?: string;
  address?: string;
  type_name?: string;
  relationship_label?: string;
  property_residency_kind?: PropertyResidencyKind;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  submitted_at: string;
  rejection_reason?: string;
}

export interface SubmitPropertyClaimPayload {
  location_id: string;
  property_relationship_type_id?: string;
  property_residency_kind: PropertyResidencyKind;
  evidence?: Record<string, unknown>;
}

interface RawEstateLocation {
  location_id: string;
  name?: string | null;
  address?: string | null;
  type_name?: string | null;
}

interface RawClaimRow {
  claim_id: string;
  location_id: string;
  property_relationship_type_id?: string;
  property_residency_kind?: PropertyResidencyKind;
  verification_status: string;
  rejection_reason?: string | null;
  created_at: string | Date;
  location_name?: string | null;
  location_address?: string | null;
  type_name?: string | null;
  relationship_label?: string | null;
}

interface RawRelationshipType {
  relationship_type_id: string;
  code: string;
  label: string;
  required_documents?: RequiredClaimDocument[];
}

function singleLineAddress(address?: string | null): string {
  if (!address) return '';
  return address
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .join(', ');
}

function mapLocation(row: RawEstateLocation): EstatePropertyOption {
  const address = singleLineAddress(row.address);
  return {
    location_id: row.location_id,
    name: row.name?.trim() || address || 'Property',
    address: address || undefined,
    type_name: row.type_name?.trim() || undefined,
  };
}

function mapClaim(row: RawClaimRow): PropertyClaim {
  const address = singleLineAddress(row.location_address);
  const status = (row.verification_status || 'pending') as PropertyClaim['status'];
  return {
    claim_id: row.claim_id,
    location_id: row.location_id,
    property_id: row.location_id,
    unit_label: row.location_name?.trim() || address || 'Property',
    address: address || undefined,
    type_name: row.type_name?.trim() || undefined,
    relationship_label: row.relationship_label?.trim() || undefined,
    property_residency_kind: row.property_residency_kind,
    status,
    submitted_at:
      row.created_at instanceof Date ? row.created_at.toISOString() : String(row.created_at),
    rejection_reason: row.rejection_reason ?? undefined,
  };
}

export async function searchEstateProperties(
  search: string,
  page = 1,
): Promise<{ properties: EstatePropertyOption[]; pages: number }> {
  const { data: body } = await tenantApi.get('/public/properties', {
    params: { search: search.trim() || undefined, page, limit: 30 },
  });
  const envelope = body as { data?: RawEstateLocation[]; pagination?: { pages?: number } };
  return {
    properties: unwrapList<RawEstateLocation>(envelope).map(mapLocation),
    pages: envelope.pagination?.pages ?? 1,
  };
}

export async function fetchRelationshipTypes(): Promise<PropertyRelationshipTypeOption[]> {
  const { data } = await tenantApi.get('/public/property-relationship-types');
  return unwrapList<RawRelationshipType>(data).map((row) => ({
    relationship_type_id: row.relationship_type_id,
    code: row.code,
    label: row.label,
    required_documents: row.required_documents ?? [],
  }));
}

export async function fetchMyClaims(): Promise<PropertyClaim[]> {
  const { data } = await tenantApi.get('/public/me/property-claims');
  return unwrapList<RawClaimRow>(data).map(mapClaim);
}

/** Approved properties are claims the estate has verified. */
export function approvedPropertiesFromClaims(claims: PropertyClaim[]): PropertyClaim[] {
  return claims.filter((c) => c.status === 'approved');
}

/** Member's approved properties — used by support, access invites, welcome flows. */
export async function fetchMyProperties(): Promise<MemberProperty[]> {
  const claims = await fetchMyClaims();
  return approvedPropertiesFromClaims(claims).map((c) => ({
    property_id: c.property_id,
    unit_label: c.unit_label,
    address: c.address,
    type_name: c.type_name,
    estate_name: c.estate_name,
    status: c.status,
  }));
}

export async function submitPropertyClaim(payload: SubmitPropertyClaimPayload): Promise<PropertyClaim> {
  const { data } = await tenantApi.post('/public/me/property-claims', payload);
  return mapClaim(unwrap<RawClaimRow>(data));
}

export async function cancelPropertyClaim(claimId: string): Promise<void> {
  await tenantApi.post(`/public/me/property-claims/${claimId}/cancel`);
}

export async function uploadClaimDocument(
  file: File | Blob,
  documentTypeId: string,
  options: { title?: string; relationshipTypeId?: string; fileName?: string },
): Promise<void> {
  const form = new FormData();
  const name = options.fileName ?? (file instanceof File ? file.name : 'document.pdf');
  form.append('file', file, name);
  form.append('document_type_id', documentTypeId);
  form.append('title', options.title ?? name);
  await tenantApi.post('/public/me/onboarding-documents', form, {
    params: options.relationshipTypeId
      ? { property_relationship_type_id: options.relationshipTypeId }
      : undefined,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}
